import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Button,
  Typography,
  Divider,
  Paper,
  IconButton,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import ProductSelectDialog from "./../../components/ProductSelectDialog";
import ProductScaleDialog from "./components/ProductScaleDialog";
import * as services from "./../../services/saleng";
import { formatNumber } from "../../utils/formatNumber";

const ProductItem = ({ product, onDelete, onEdit }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    border="1px solid #eee"
    borderRadius={2}
    mb={1}
  >
    <Box
      flex={1}
      sx={{
        cursor: "pointer",
        "&:hover": {
          bgcolor: "grey.100",
        },
      }}
      p={3}
      onClick={() => onEdit?.(product)}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight="bold">
              {product.index}. {product.name}
            </Typography>
            <EditIcon fontSize="small" />
          </Box>
          <Typography color="text.secondary" mt={1}>
            รวม: {product.total.toFixed(2)} &nbsp; หัก:{" "}
            {product.rest.toFixed(2)} &nbsp; = {product.net.toFixed(2)}
          </Typography>
        </Box>
        <Box>
          <Chip
            label={`${product.price.toFixed(2)} บาท/กก.`}
            color="primary"
            size="medium"
            sx={{ fontWeight: "bold", fontSize: 14, px: 1 }}
          />
        </Box>
      </Box>
    </Box>
    <Box>
      <IconButton onClick={() => onDelete?.(product)}>
        <DeleteIcon color="error" />
      </IconButton>
    </Box>
  </Box>
);

const ProductPanel = ({
  title,
  onAdd,
  products = [],
  summary,
  extra,
  onDelete,
  onEdit,
}) => (
  <Paper
    sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}
  >
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography fontWeight="bold">{title}</Typography>
      <Button
        variant="outlined"
        size="large"
        startIcon={<AddIcon />}
        onClick={onAdd}
        disabled={title === "วัตถุดิบต้นทาง" && products.length > 0}
      >
        เพิ่มสินค้า
      </Button>
    </Box>
    <Divider sx={{ my: 2 }} />
    <Box flex={1} overflow="auto">
      {products.length === 0 ? (
        <Typography color="text.secondary">ยังไม่มีสินค้า</Typography>
      ) : (
        products.map((p) => (
          <ProductItem
            key={p.index}
            product={p}
            onDelete={onDelete}
            onEdit={onEdit}
          />
        ))
      )}
    </Box>
    {extra && (
      <>
        <Divider />
        <Box mt={2} mb={1}>
          {extra}
        </Box>
      </>
    )}
    <Divider sx={{ mt: 1, mb: 0.2 }} />
    <Divider sx={{ mb: 2 }} />
    <Box display="flex" justifyContent="space-between" flexWrap="wrap">
      {summary.map((item, i) => (
        <Typography key={i}>
          {item.label}:{" "}
          {typeof item.value === "number" ? item.value.toFixed(2) : item.value}
        </Typography>
      ))}
    </Box>
  </Paper>
);

export default function IndexPage() {
  const [productsLeft, setProductsLeft] = useState([]);
  const [productsRight, setProductsRight] = useState([]);
  const [data, setData] = useState([]);

  const [summary1, setSummary1] = useState([
    { label: "หน่วย", value: "กก." },
    { label: "รวม", value: 0 },
    { label: "หัก", value: 0 },
    { label: "สุทธิ", value: 0 },
  ]);

  const [summary2, setSummary2] = useState([
    { label: "หน่วย", value: "กก." },
    { label: "รวม", value: 0 },
    { label: "หัก", value: 0 },
    { label: "สุทธิ", value: 0 },
  ]);

  const [extra, setExtra] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dialogSide, setDialogSide] = useState("left");
  const [openMainProdDialog, setOpenMainProdDialog] = useState(false);
  const [openProductScaleDialog, setOpenProductScaleDialog] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await services.getInventory();
        const rawData = res.data.data;
        console.log('rawData',rawData)

        // แปลงข้อมูลจาก rawData
        const transformed = rawData.map((item, index) => {
          return {
            id: index + 1,
            productId: item.productId,
            title: item.product?.title || "",
            basePrice: item.product?.price || 0,
            price: item.averageCost || 0,
            visible: item.quantity > 0,
            branchId: item.branchId,
            categoryId: item.product?.categoryId || null,
            subCategoryId: item.product?.subCategoryId || null,
            category: {
              id: item.product?.category?.id,
              name: item.product?.category?.name,
            },
            subCategory: {
              id: item.product?.subCategory?.id,
              name: item.product?.subCategory?.name,
            },
            image: item.product?.image || null,
            stock: item.quantity || 0,
          };
        });

        console.log("transformed products", transformed);
        setData(transformed);
      } catch (err) {
        console.error("โหลดข้อมูลไม่สำเร็จ", err);
      }
    }

    fetchData();
  }, []);

  const handleAddLeft = () => {
    setDialogSide("left");
    setOpenMainProdDialog(true);
  };

  const handleAddRight = () => {
    setDialogSide("right");
    setOpenMainProdDialog(true);
  };

  const updateSummary = (list, isLeft) => {
    const sum = [
      { label: "หน่วย", value: "กก." },
      { label: "รวม", value: list.reduce((s, p) => s + p.total, 0) },
      { label: "หัก", value: list.reduce((s, p) => s + p.rest, 0) },
      { label: "สุทธิ", value: list.reduce((s, p) => s + p.net, 0) },
    ];
    if (isLeft) {
      setSummary1(sum);
      setExtra(sum[3].value - summary2[3]?.value || 0);
    } else {
      setSummary2(sum);
      setExtra(summary1[3]?.value - sum[3].value || 0);
    }
  };

  const handleDelete = (side, productToDelete) => {
    if (side === "left") {
      // ลบสินค้าฝั่งซ้าย
      const newLeft = productsLeft
        .filter((p) => p.index !== productToDelete.index)
        .map((p, i) => ({ ...p, index: i + 1 }));
      setProductsLeft(newLeft);
      updateSummary(newLeft, true);

      // 🔹 ถ้ามีสินค้าฝั่งขวา ให้รีคำนวณราคา
      if (productsRight.length > 0) {
        const totalCostLeft = newLeft.reduce(
          (sum, p) => sum + p.net * p.price,
          0
        );
        const totalNetRight = productsRight.reduce((sum, p) => sum + p.net, 0);
        const avgCostPerKg =
          totalCostLeft > 0 && totalNetRight > 0
            ? totalCostLeft / totalNetRight
            : 0;

        const newRight = productsRight.map((p) => ({
          ...p,
          price: avgCostPerKg,
        }));
        setProductsRight(newRight);
        updateSummary(newRight, false);
      }
    } else {
      // ลบสินค้าฝั่งขวา
      const newRight = productsRight
        .filter((p) => p.index !== productToDelete.index)
        .map((p, i) => ({ ...p, index: i + 1 }));

      // 🔹 คำนวณราคาฝั่งขวาใหม่ตามฝั่งซ้าย
      const totalCostLeft = productsLeft.reduce(
        (sum, p) => sum + p.net * p.price,
        0
      );
      const totalNetRight = newRight.reduce((sum, p) => sum + p.net, 0);
      const avgCostPerKg =
        totalCostLeft > 0 && totalNetRight > 0
          ? totalCostLeft / totalNetRight
          : 0;

      const updatedRight = newRight.map((p) => ({ ...p, price: avgCostPerKg }));

      setProductsRight(updatedRight);
      updateSummary(updatedRight, false);
    }
  };

  const handleEdit = (side, product) => {
    console.log("product", product);
    setSelectedProduct({ ...product, side });
    setDialogSide(side);
    setOpenProductScaleDialog(true);
  };

  const handleSubmit = async () => {
    try {
      const data1 = productsLeft[0]; // ฝั่งซ้ายมีแค่ 1

      if (!data1) {
        console.error("❌ ไม่พบสินค้าในฝั่งซ้าย");
        return;
      }

      const payloadSell = {
        productId: data1.id,
        quantity: data1.total,
        saleTransactionId: null,
        unitCost: data1.price,
        note: "แปลงสภาพ",
      };

      await services.updateStockSell(payloadSell);

      for (const data2 of productsRight) {
        // ส่ง transform สำหรับแต่ละคู่ (ฝั่งซ้ายเดียวกัน)
        const payloadTransform = {
          originWeight: data1.total,
          originId: data1.id,
          destinationWeight: data2.total,
          destinationId: data2.id,
        };

        const payloadReceive = {
          productId: data2.id,
          quantity: data2.total,
          saleTransactionId: null,
          unitCost: data2.price,
          note: "แปลงสภาพ",
        };

        await services.createTransformTransaction(payloadTransform);
        await services.updateStock(payloadReceive);
      }

    } catch (error) {
      console.error("❌ เกิดข้อผิดพลาด:", error);
    }
  };

  return (
    <Box sx={{ p: 2, height: "100vh", boxSizing: "border-box" }}>
      <AppBreadcrumbs />
      <Grid
        container
        spacing={2}
        alignItems="stretch"
        justifyContent="center"
        pt={2}
        sx={{ height: "calc(100% - 48px)" }}
      >
        <Grid item xs={12} md={5.5} sx={{ height: "85%" }}>
          <ProductPanel
            title="วัตถุดิบต้นทาง"
            onAdd={handleAddLeft}
            products={productsLeft}
            summary={summary1}
            onDelete={(p) => handleDelete("left", p)}
            onEdit={(p) => handleEdit("left", p)}
          />
        </Grid>
        <Grid item xs={12} md={1} textAlign="center" alignSelf="center">
          <DoubleArrowIcon fontSize="large" />
        </Grid>
        <Grid item xs={12} md={5.5} sx={{ height: "85%" }}>
          <ProductPanel
            title="วัตถุดิบปลายทาง"
            onAdd={handleAddRight}
            products={productsRight}
            summary={summary2}
            extra={
              <Typography
                sx={{
                  color: extra !== 0 ? "red" : "green",
                  fontWeight: "bold",
                }}
              >
                {extra >= 0
                  ? `สูญเสีย: ${formatNumber(extra)} `
                  : `ส่วนเกิน ${formatNumber(Math.abs(extra))}`}
              </Typography>
            }
            onDelete={(p) => handleDelete("right", p)}
            onEdit={(p) => handleEdit("right", p)}
          />
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={7}>
              <Button
                variant="contained"
                fullWidth
                color="success"
                sx={{ height: 60 }}
                // onClick={() => console.log(">>> ", productsLeft, productsRight)}
                onClick={handleSubmit}
                disabled={
                  !(productsLeft.length > 0 && productsRight.length > 0)
                }
              >
                ยืนยัน
              </Button>
            </Grid>
            <Grid item xs={12} md={5}>
              <Button
                variant="contained"
                fullWidth
                sx={{ height: 60 }}
                color="error"
              >
                ยกเลิก
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <ProductSelectDialog
        open={openMainProdDialog}
        onClose={() => setOpenMainProdDialog(false)}
        onSelect={(prod) => {
          setSelectedProduct(prod);
          setOpenMainProdDialog(false);
          setOpenProductScaleDialog(true);
        }}
        products={data}
      />

      <ProductScaleDialog
        open={openProductScaleDialog}
        onClose={() => {
          setOpenProductScaleDialog(false);
          setSelectedProduct(null);
        }}
        onConfirm={(scale) => {
          if (!selectedProduct) return;

          const side = dialogSide;
          const isLeft = side === "left";

          // เลือก list ของฝั่งที่ต้องแก้ไข
          let productList = isLeft ? [...productsLeft] : [...productsRight];

          // 🔹 อัปเดตสินค้าที่เลือก
          const updatedProduct = {
            ...selectedProduct,
            total: scale.weight,
            rest: scale.deduct,
            net: scale.net,
            price: selectedProduct.price, // เริ่มต้น
          };

          // 🔹 หา index ของสินค้าที่แก้ไข
          const index = productList.findIndex(
            (p) => p.index === selectedProduct.index
          );

          if (index >= 0) {
            productList[index] = updatedProduct;
          } else {
            productList.push({
              ...updatedProduct,
              index: productList.length + 1,
              name: selectedProduct.title,
            });
          }

          // 🔹 ถ้าเป็นฝั่งขวา ให้คำนวณราคาใหม่จากฝั่งซ้าย
          if (!isLeft) {
            // total ของฝั่งซ้าย
            const totalCostLeft = productsLeft.reduce(
              (sum, p) => sum + p.net * p.price,
              0
            );

            // รวม net ฝั่งขวา (รวม updatedProduct ที่เพิ่งเพิ่ม)
            const totalNetRight = productList.reduce(
              (sum, p) => sum + p.net,
              0
            );

            const avgCostPerKg =
              totalCostLeft > 0 && totalNetRight > 0
                ? totalCostLeft / totalNetRight
                : 0;

            // 🔹 อัปเดตราคาทุกตัวในฝั่งขวา
            productList = productList.map((p) => ({
              ...p,
              price: avgCostPerKg,
            }));
          } else {
            // กรณีแก้ไขฝั่งซ้าย → อัปเดตราคาฝั่งขวา
            const totalCostLeft = productList.reduce(
              (sum, p) => sum + p.net * p.price,
              0
            );
            const totalNetRight = productsRight.reduce(
              (sum, p) => sum + p.net,
              0
            );
            const avgCostPerKg =
              totalCostLeft > 0 && totalNetRight > 0
                ? totalCostLeft / totalNetRight
                : 0;

            const updatedRight = productsRight.map((p) => ({
              ...p,
              price: avgCostPerKg,
            }));

            setProductsRight(updatedRight);
            updateSummary(updatedRight, false);
          }

          // 🔹 รีเซ็ต index
          const reindexed = productList.map((p, i) => ({ ...p, index: i + 1 }));

          // 🔹 อัปเดต state
          if (isLeft) {
            setProductsLeft(reindexed);
            updateSummary(reindexed, true);
          } else {
            setProductsRight(reindexed);
            updateSummary(reindexed, false);
          }

          setOpenProductScaleDialog(false);
          setSelectedProduct(null);
        }}
        init={{ ...selectedProduct, dialogSide }}
      />
    </Box>
  );
}
